import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const Pagination = ({
    currentPage = 1,
    totalPages = 1,
    itemsPerPage = 10,
    totalItems = 0,
    onPageChange,
    startIndex = 0,
    endIndex = 0,
}) => {
    // Handle invalid pagination state
    const validCurrentPage = Math.max(1, Math.min(currentPage, totalPages));
    const validTotalPages = Math.max(1, totalPages);
    const validEndIndex = Math.max(endIndex, startIndex);
    const validTotalItems = Math.max(0, totalItems);
    // Generate page numbers for pagination
    const getPageNumbers = () => {
        const pageNumbers = [];
        const maxVisiblePages = 5;
        
        if (validTotalPages <= maxVisiblePages) {
            for (let i = 1; i <= validTotalPages; i++) {
                pageNumbers.push(i);
            }
        } else {
            if (validCurrentPage <= 3) {
                for (let i = 1; i <= 5; i++) {
                    pageNumbers.push(i);
                }
                if (validTotalPages > 5) {
                    pageNumbers.push('...');
                    pageNumbers.push(validTotalPages);
                }
            } else if (validCurrentPage >= validTotalPages - 2) {
                pageNumbers.push(1);
                if (validTotalPages > 5) {
                    pageNumbers.push('...');
                }
                for (let i = validTotalPages - 4; i <= validTotalPages; i++) {
                    pageNumbers.push(i);
                }
            } else {
                pageNumbers.push(1);
                pageNumbers.push('...');
                for (let i = validCurrentPage - 1; i <= validCurrentPage + 1; i++) {
                    pageNumbers.push(i);
                }
                pageNumbers.push('...');
                pageNumbers.push(validTotalPages);
            }
        }
        return pageNumbers;
    };

    return (
        validTotalPages > 1 && (
            <div className="flex items-center justify-between bg-white px-4 py-3 sm:px-6 rounded-lg shadow-md">
                <div className="flex flex-1 justify-between sm:hidden">
                    <button
                        onClick={() => onPageChange(Math.max(validCurrentPage - 1, 1))}
                        disabled={validCurrentPage === 1}
                        className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Previous
                    </button>
                    <span className="text-sm text-gray-700">
                        Page <span className="font-medium">{validCurrentPage}</span> of <span className="font-medium">{validTotalPages}</span>
                    </span>
                    <button
                        onClick={() => onPageChange(Math.min(validCurrentPage + 1, validTotalPages))}
                        disabled={validCurrentPage === validTotalPages}
                        className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Next
                    </button>
                </div>
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                    <div>
                        <p className="text-sm text-gray-700">
                            Showing{' '}
                            <span className="font-medium">{startIndex + 1}</span>
                            {' '}to{' '}
                            <span className="font-medium">
                                {Math.min(validEndIndex, validTotalItems)}
                            </span>
                            {' '}of{' '}
                            <span className="font-medium">{validTotalItems}</span>
                            {' '}results
                        </p>
                    </div>
                    <div>
                        <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                            <button
                                onClick={() => onPageChange(Math.max(validCurrentPage - 1, 1))}
                                disabled={validCurrentPage === 1}
                                className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <span className="sr-only">Previous</span>
                                <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                            </button>
                            
                            {getPageNumbers().map((pageNumber, index) => (
                                pageNumber === '...' ? (
                                    <span
                                        key={`ellipsis-${index}`}
                                        className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 focus:outline-offset-0"
                                    >
                                        ...
                                    </span>
                                ) : (
                                    <button
                                        key={pageNumber}
                                        onClick={() => onPageChange(pageNumber)}
                                        className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 ${validCurrentPage === pageNumber
                                            ? 'z-10 bg-teal-600 text-white focus:bg-teal-600'
                                            : 'text-gray-900'
                                        }`}
                                    >
                                        {pageNumber}
                                    </button>
                                )
                            ))}

                            <button
                                onClick={() => onPageChange(Math.min(validCurrentPage + 1, validTotalPages))}
                                disabled={validCurrentPage === validTotalPages}
                                className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <span className="sr-only">Next</span>
                                <ChevronRight className="h-5 w-5" aria-hidden="true" />
                            </button>
                        </nav>
                    </div>
                </div>
            </div>
        )
    );
};

export default Pagination;